import type { FC } from 'react';

import { InfoCircle } from '@untitledui/icons';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { AlertTriangle } from '@untitledui/icons/AlertTriangle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { XClose } from '@untitledui/icons/XClose';
import { toast } from 'sonner';

import { Button } from '@/components/base/buttons/button';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

const innerBorderVariants = {
  'brand-primary': {
    default:
      'absolute -inset-1.5 rounded-full border-2 border-fg-brand-primary/30',
    sm: 'absolute -inset-1 rounded-full border-2 border-fg-brand-primary/30',
  },
  'error-primary': {
    default:
      'absolute -inset-1.5 rounded-full border-2 border-fg-error-primary/30',
    sm: 'absolute -inset-1 rounded-full border-2 border-fg-error-primary/30',
  },
  'brand-warning': {
    default:
      'absolute -inset-1.5 rounded-full border-2 border-fg-warning-primary/30',
    sm: 'absolute -inset-1 rounded-full border-2 border-fg-warning-primary/30',
  },
} as const;

const outerBorderVariants = {
  'brand-primary': {
    default:
      'absolute -inset-3 rounded-full border-2 border-fg-brand-primary/10',
    sm: 'absolute -inset-2.25 rounded-full border-2 border-fg-brand-primary/10',
  },
  'error-primary': {
    default:
      'absolute -inset-3 rounded-full border-2 border-fg-error-primary/10',
    sm: 'absolute -inset-2.25 rounded-full border-2 border-fg-error-primary/10',
  },
  'brand-warning': {
    default:
      'absolute -inset-3 rounded-full border-2 border-fg-warning-primary/10',
    sm: 'absolute -inset-2.25 rounded-full border-2 border-fg-warning-primary/10',
  },
} as const;

const iconVariants = {
  'brand-primary': 'relative size-5 text-fg-brand-primary',
  'error-primary': 'relative size-5 text-fg-error-primary',
  'brand-warning': 'relative size-5 text-fg-warning-primary',
} as const;

export const WrappedInfoCircle: React.FC<{
  color: 'brand-primary' | 'error-primary' | 'brand-warning';
  ringSize?: 'default' | 'sm';
}> = ({ color, ringSize = 'default' }) => (
  <div className="relative size-5 shrink-0">
    <div className={innerBorderVariants[color][ringSize]} />
    <div className={outerBorderVariants[color][ringSize]} />
    <InfoCircle className={iconVariants[color]} />
  </div>
);

const colorIcons: Record<
  IconNotificationProps['color'],
  FC<{ className?: string }>
> = {
  error: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle,
  'brand-primary': () => <WrappedInfoCircle color="brand-primary" />,
  'error-primary': () => <WrappedInfoCircle color="error-primary" />,
  'brand-warning': () => <WrappedInfoCircle color="brand-warning" />,
};

export interface IconNotificationProps {
  title?: string;
  description?: string;
  color:
    | 'error'
    | 'warning'
    | 'success'
    | 'brand-primary'
    | 'error-primary'
    | 'brand-warning';
  iconSize?: 'sm' | 'md' | 'lg' | 'xl';
  confirmLabel?: string;
  dismissLabel?: string;
  hideDismissLabel?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  onDismiss?: () => void;
  className?: string;
  actionButton?: { label: string; onClick: () => void; isLoading?: boolean };
  /** Optional `data-testid` on the notification root (e.g. for e2e). */
  dataTestId?: string;
}

export const IconNotification = ({
  className,
  title,
  iconSize = 'md',
  description,
  color,
  confirmLabel,
  dismissLabel = 'Dismiss',
  hideDismissLabel,
  onClose,
  onConfirm,
  actionButton,
  onDismiss,
  dataTestId,
}: IconNotificationProps) => {
  return (
    <div
      data-testid={dataTestId}
      className={cx(
        'flex w-[400px] gap-4 rounded-xl p-4 shadow-lg',
        color === 'brand-primary'
          ? 'bg-brand-primary'
          : color === 'error-primary'
            ? 'bg-error-primary'
            : color === 'brand-warning'
              ? 'bg-utility-warning-50'
              : 'bg-primary ring-1 ring-primary ring-inset',
        className,
      )}
    >
      <FeaturedIcon
        icon={colorIcons[color]}
        color={color}
        theme="light"
        size={iconSize}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          {title && (
            <p className="text-sm font-semibold text-primary">{title}</p>
          )}
          {description && (
            <p
              className={cx(
                'text-sm text-primary',
                color === 'error-primary' && 'font-semibold',
              )}
            >
              {description}
            </p>
          )}
        </div>

        {(confirmLabel || !hideDismissLabel) && (
          <div className="flex gap-3">
            {!hideDismissLabel && (
              <Button
                size="sm"
                color="link-gray"
                onClick={onDismiss ?? onClose}
              >
                {dismissLabel}
              </Button>
            )}
            {confirmLabel && (
              <Button size="sm" color="link-color" onClick={onConfirm}>
                {confirmLabel}
              </Button>
            )}
          </div>
        )}
      </div>

      {actionButton && (
        <Button
          size="sm"
          color="primary-destructive"
          onClick={actionButton?.onClick}
          isLoading={actionButton?.isLoading}
        >
          {actionButton?.label}
        </Button>
      )}

      {onClose && (
        <button
          type="button"
          aria-label="Close notification"
          onClick={onClose}
          className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center text-fg-quaternary hover:text-fg-quaternary_hover"
        >
          <XClose className="size-5" />
        </button>
      )}
    </div>
  );
};

export type NotifyOptions = Omit<IconNotificationProps, 'onClose'>;

export function notify(options: NotifyOptions) {
  return toast.custom((t) => (
    <IconNotification
      {...options}
      onClose={() => toast.dismiss(t)}
      onDismiss={options.onDismiss ?? (() => toast.dismiss(t))}
    />
  ));
}
